import type { MapFormState, SearchContext } from "../types";
import { AdvancedField, AdvancedSection } from "./AdvancedSection";
import { Examples } from "./Examples";
import { FIELD, HELP, LABEL, TEXT_ACTION } from "./styles";

const QUESTION_EXAMPLES = [
  "What was the main finding and sample size?",
  "What methods were used, and on what data?",
  "What limitations do the authors acknowledge?",
];

const SCHEMA_EXAMPLE = `{"type":"object","properties":{"finding":{"type":"string"},"sample_size":{"type":["number","null"]}},"required":["finding","sample_size"],"additionalProperties":false}`;

/** How many advanced map flags differ from their defaults. */
const countActiveAdvanced = (form: MapFormState): number =>
  [
    form.limit.trim() !== "",
    form.offset.trim() !== "",
    form.outputSchema.trim() !== "",
  ].filter(Boolean).length;

/** Light client-side validity check so schema typos fail before the run. */
const schemaIsInvalid = (value: string): boolean => {
  if (!value.trim()) return false;
  try {
    JSON.parse(value);
    return false;
  } catch {
    return true;
  }
};

/**
 * Map runs over a search result set, so this form is gated: without a live
 * search result it walks the user back to Search first.
 */
export const MapControls = ({
  form,
  context,
  onChange,
  onSubmit,
  onGoToSearch,
  disabled = false,
}: {
  form: MapFormState;
  context: SearchContext | null;
  onChange: (next: MapFormState) => void;
  onSubmit: () => void;
  onGoToSearch: () => void;
  disabled?: boolean;
}) => {
  if (!context) {
    return (
      <div className="border-t border-zinc-200/70 pt-4">
        <p className="text-base leading-7 text-zinc-900">Run a search first.</p>
        <p className="mt-1 max-w-sm text-sm leading-6 text-zinc-500">
          Map sends an LLM reader into every paper of a result set and asks
          each one the same question. Search for a topic, then come back here
          to interrogate the results.
        </p>
        <button type="button" onClick={onGoToSearch} className={`${TEXT_ACTION} mt-3 text-zinc-700`}>
          Go to Search →
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="border-l-2 border-zinc-200 pl-3">
        <p className={LABEL}>Mapping over</p>
        <p className="mt-0.5 truncate text-sm leading-6 text-zinc-700">
          {context.papers.length} paper{context.papers.length === 1 ? "" : "s"} from{" "}
          <span className="italic">“{context.query}”</span>
        </p>
        <p className="mt-0.5 font-mono text-2xs text-zinc-400">{context.resultsId}</p>
      </div>

      <div>
        <label
          htmlFor="playground-map-question"
          className={LABEL}
        >
          Question for every paper
        </label>
        <input
          className={`${FIELD} mt-1`}
          id="playground-map-question"
          value={form.question}
          disabled={disabled}
          aria-describedby={disabled ? "usage-alert-playground-maps" : undefined}
          onChange={(e) => onChange({ ...form, question: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !disabled) onSubmit();
          }}
        />
        <p className={HELP}>
          Be specific — enumerate the fields you want extracted. Each paper
          gets its own reader, so runs take ~30–90s.
        </p>
      </div>

      <Examples
        items={QUESTION_EXAMPLES.map((value) => ({ value }))}
        onPick={(question) => {
          if (!disabled) onChange({ ...form, question });
        }}
      />

      <AdvancedSection
        title="Advanced options"
        activeCount={countActiveAdvanced(form)}
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          <AdvancedField label="Papers to process" flag="-n" htmlFor="pg-map-n">
            <input
              id="pg-map-n"
              className={`${FIELD} mt-1`}
              value={form.limit}
              placeholder={`All (${context.papers.length})`}
              inputMode="numeric"
              onChange={(e) => onChange({ ...form, limit: e.target.value })}
            />
          </AdvancedField>
          <AdvancedField label="Skip first" flag="--offset" htmlFor="pg-map-offset">
            <input
              id="pg-map-offset"
              className={`${FIELD} mt-1`}
              value={form.offset}
              placeholder="0"
              inputMode="numeric"
              onChange={(e) => onChange({ ...form, offset: e.target.value })}
            />
          </AdvancedField>
        </div>
        <AdvancedField
          label="Structured output (JSON Schema)"
          flag="--output-schema"
          htmlFor="pg-map-schema"
        >
          <textarea
            id="pg-map-schema"
            value={form.outputSchema}
            rows={4}
            spellCheck={false}
            placeholder={SCHEMA_EXAMPLE}
            onChange={(e) => onChange({ ...form, outputSchema: e.target.value })}
            className="pg-code mt-1 w-full resize-y px-3 py-2 font-mono text-xs leading-5 text-zinc-800 placeholder:text-zinc-400 focus:outline-none"
          />
          {schemaIsInvalid(form.outputSchema) ? (
            <p className="mt-1 text-2xs text-red-700" role="alert">
              Not valid JSON — the run would fail every paper.
            </p>
          ) : (
            <p className={HELP}>
              Each paper returns one strict JSON value matching this schema.
              Use <code className="font-mono">required</code> +{" "}
              <code className="font-mono">additionalProperties:false</code> for
              exact keys.
            </p>
          )}
        </AdvancedField>
      </AdvancedSection>
    </div>
  );
};
